import type { WidgetDefinition } from '../WidgetDefinition';
import { faBuildingMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faBuildingMagnifyingGlass';
import { generatorRoutine } from '@thinkalpha/ui-core/routine/index';
import type { SearchAlphaWidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import { createDeferredIdeaHydration, shouldDeferIdeaHydration } from 'src/lib/widget/deferredIdeaHydration';
import { createDefaultSearchAlphaWidget } from 'src/store/defaults/widget';
import type { SearchAlphaWidgetViewModel } from 'src/store/types';

class SearchAlphaWidgetDefinition implements WidgetDefinition<SearchAlphaWidgetViewModel, SearchAlphaWidgetModel> {
    type = 'searchalpha' as const;
    nameKey = 'widgets::searchalpha::name' as const;
    roles = ['widgets::searchalpha', 'widgets::search-alpha'];
    icon = faBuildingMagnifyingGlass;
    category = 'widgets::categories::research' as const;
    defaultDimensions = { h: 0.5, w: 0.6, x: 0.2, y: 0.05 };

    /**
     * Converts a SearchAlpha widget from contract model to view model.
     *
     * Handles three serialized forms of `idea`:
     *  - string id: clean saved idea, deferred to render-time hydration
     *  - { id, ... }: dirty draft of a saved idea, deferred to render-time hydration
     *  - { ... } without id: new unsaved idea
     */
    toViewModel = generatorRoutine(function* (_ctx, widget: SearchAlphaWidgetModel) {
        const { idea: incomingIdea, ...restWidget } = widget;

        let idea: SearchAlphaWidgetViewModel['idea'] | undefined;
        let isIdeaDirty = false;
        const ideaHydration = shouldDeferIdeaHydration(incomingIdea)
            ? createDeferredIdeaHydration(incomingIdea)
            : undefined;

        if (!ideaHydration && incomingIdea && typeof incomingIdea !== 'string') {
            idea = incomingIdea;
            isIdeaDirty = true;
        }

        return createDefaultSearchAlphaWidget({
            ...restWidget,
            ...(idea ? { idea } : {}),
            isIdeaDirty,
            ...(ideaHydration ? { ideaHydration } : {}),
        });
    });

    /**
     * Converts a SearchAlpha widget from view model to contract model
     */
    toContractModel(widget: SearchAlphaWidgetViewModel): SearchAlphaWidgetModel {
        const { idea, ideaHydration, isIdeaDirty, ...restWidget } = widget;

        return {
            ...restWidget,
            idea:
                ideaHydration && ideaHydration.status !== 'ready'
                    ? ideaHydration.source
                    : idea?.id && isIdeaDirty === false
                      ? idea.id
                      : idea,
            type: 'searchalpha',
        };
    }
}

export const searchAlphaWidgetDefinition = new SearchAlphaWidgetDefinition();
